import { AddEditUserRoleIds, AllAdminRoleIds, AllStudentRoleIds } from "@/constants/userRoles";
import { DashboardTabsEnum } from "@/enums/affiliation";
import { ActiveInactiveStatusEnum } from "@/enums/global";
import { AllUserRoleIdsEnum } from "@/enums/userManagement";
import { PaginatedStoreIdsEnum } from "@/enums/stores";
import {
  deepCopyObject,
  getIdNamePairsFromEnum,
  getUniqueObjectsByKey,
  showCustomMessage,
} from "@/methods/utils";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { createPaginatedCardsStore } from "@/stores/uiComponents/paginatedCardsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { AffiliationCourseBatch, ParentAffiliationStore } from "@/types/affiliation";
import { IdNamePair } from "@/types/global";
import { defineStore, storeToRefs } from "pinia";

function getInitialParentAffiliationStore(): ParentAffiliationStore {
  const initialParentAffiliationStore: ParentAffiliationStore = {
    SelectedAffiliation: null,
    IsSelectorActive: false,
    AffiliationStatistics: {},
    AffiliationOptions: null,
    AffiliationCourseBatchTree: null,
    BatchSubjects: {},
    BatchesInfo: {},
    AffiliationCourses: {},
    CurrentAffiliationStatus: ActiveInactiveStatusEnum.ACTIVE,
    ReloadAffiliationsKey: 0,
    DashboardActiveTab: DashboardTabsEnum.REPORTS,
    DataLoading: false,
  };
  return JSON.parse(JSON.stringify(initialParentAffiliationStore));
}
export const useParentAffiliationStore = defineStore("parentAffiliationStore", {
  state: (): ParentAffiliationStore => getInitialParentAffiliationStore(),
  getters: {
    DefaultAffiliation(): IdNamePair | null {
      if (this.AffiliationOptions === null) {
        return null;
      }
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      let defaultAffiliation = this.AffiliationOptions.find(
        (affiliation) => affiliation.Id === UserInfo.value.AffiliationId,
      );
      if (defaultAffiliation === undefined && this.AffiliationOptions.length > 0) {
        defaultAffiliation = this.AffiliationOptions[0];
      }
      return defaultAffiliation || null;
    },
    ActiveAffiliation(): IdNamePair | null {
      return this.IsSelectorActive && this.SelectedAffiliation !== null
        ? this.SelectedAffiliation
        : this.DefaultAffiliation;
    },
  },
  actions: {
    async initializeAffiliationSelector() {
      await this.getAffiliationOptions();
    },
    async onAffiliationStatusChange(statusId: number) {
      this.CurrentAffiliationStatus = statusId;
      await this.getAffiliationOptions();
    },
    async getAffiliationOptions() {
      this.DataLoading = true;
      this.AffiliationOptions = null;
      this.SelectedAffiliation = null;
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { adminApi } = storeToRefs(apiServicesStore);
      const onlyActive = this.CurrentAffiliationStatus === ActiveInactiveStatusEnum.ACTIVE;
      const response = await adminApi.value.getAllAffiliations(UserInfo.value.UserId, onlyActive);
      if (response.isSuccess) {
        this.AffiliationOptions = response.data;
      }
      if (this.DefaultAffiliation !== null) {
        this.updateSelectedAffiliation(this.DefaultAffiliation.Id);
      }
      this.ReloadAffiliationsKey += 1;
      this.DataLoading = false;
    },
    updateSelectedAffiliation(affiliationId: number) {
      if (!this.AffiliationOptions) {
        return;
      }
      const matchedAffiliation = this.AffiliationOptions.find(
        (affiliation) => affiliation.Id === affiliationId,
      );
      if (matchedAffiliation) {
        this.SelectedAffiliation = matchedAffiliation;
      }
      this.ReloadAffiliationsKey += 1;
    },
    // List Deactivate/Activate (BR-UI-013) — called by ModalConfirmDeactivateAffiliation's
    // confirm button; returns success so the modal can manage its loading/dismiss states.
    async toggleAffiliationStatus(affiliationId: number, isActive: boolean): Promise<boolean> {
      const apiServicesStore = useApiServicesStore();
      const { adminApi } = storeToRefs(apiServicesStore);
      const response = await adminApi.value.updateAffiliationStatus(affiliationId, isActive);
      if (!response.isSuccess) {
        showCustomMessage({ messageText: response.errorMessage, messageType: "danger" });
        return false;
      }
      showCustomMessage({
        messageText: isActive ? "Affiliation activated." : "Affiliation deactivated.",
        messageType: "success",
      });
      // Refetch selector options so the status change shows without a page reload
      // (getAffiliationOptions bumps ReloadAffiliationsKey itself).
      await this.getAffiliationOptions();
      return true;
    },
    async loadDetailedAffiliations(onlyActive: boolean) {
      const storeId = PaginatedStoreIdsEnum.MANAGE_AFFILIATIONS;
      const usePaginatedCardsStore = createPaginatedCardsStore(storeId);
      const paginatedCardsStore = usePaginatedCardsStore();
      const { CurrentApiPaginationInfo, StoreSettings } = storeToRefs(paginatedCardsStore);
      if (!CurrentApiPaginationInfo.value) {
        return [];
      }
      const apiPaginationInfo = CurrentApiPaginationInfo.value;
      const apiServicesStore = useApiServicesStore();
      const { adminApi } = storeToRefs(apiServicesStore);
      const response = await adminApi.value.getAllDetailedAffiliations(
        onlyActive,
        apiPaginationInfo.pageNumber,
        StoreSettings.value.ApiPageSize,
      );
      if (!response.isSuccess) {
        showCustomMessage({ messageText: response.errorMessage, messageType: "danger" });
        return [];
      }
      apiPaginationInfo.fetchedCount += response.data.length;
      apiPaginationInfo.availableCount = response.count ?? 0;
      apiPaginationInfo.totalPages = response.pages ?? 0;
      return response.data;
    },
    async getAffiliationStatistics(affiliationId: number) {
      if (this.AffiliationStatistics[affiliationId] === undefined) {
        const apiServicesStore = useApiServicesStore();
        const { affiliationBatchApi } = storeToRefs(apiServicesStore);
        const response = await affiliationBatchApi.value.getAffiliationStatistics(affiliationId);
        this.AffiliationStatistics[affiliationId] = response.isSuccess ? response.data : null;
      }
      return this.AffiliationStatistics[affiliationId];
    },
    async getCoursesForAffiliation(affiliationId: number | null = null) {
      if (affiliationId === null) {
        affiliationId = this.ActiveAffiliation ? this.ActiveAffiliation.Id : null;
      }
      if (affiliationId === null) {
        return [];
      }
      if (this.AffiliationCourses[affiliationId] === undefined) {
        const apiServicesStore = useApiServicesStore();
        const { affiliationBatchApi } = storeToRefs(apiServicesStore);
        const response = await affiliationBatchApi.value.getCoursesForAffiliation(affiliationId);
        if (response.isSuccess) {
          this.AffiliationCourses[affiliationId] = response.data;
        } else {
          showCustomMessage({ messageText: "No active courses found.", messageType: "warning" });
          this.AffiliationCourses[affiliationId] = [];
        }
      }
      return this.AffiliationCourses[affiliationId] ? this.AffiliationCourses[affiliationId] : [];
    },
    async getAffiliationCourseBatchTree(affiliationId: number) {
      if (
        this.AffiliationCourseBatchTree !== null &&
        this.AffiliationCourseBatchTree !== undefined
      ) {
        const matchingAffiliation = this.AffiliationCourseBatchTree.find(
          (affiliation) => affiliation.Id === affiliationId,
        );
        if (matchingAffiliation !== undefined) {
          return this.AffiliationCourseBatchTree;
        }
      }
      this.DataLoading = true;
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { affiliationBatchApi } = storeToRefs(apiServicesStore);
      const resolvedAffiliationId = affiliationId ?? UserInfo.value.AffiliationId;
      // No-affiliation users have no course-batch tree — bail out cleanly.
      if (resolvedAffiliationId === null) {
        this.DataLoading = false;
        return;
      }
      const response =
        await affiliationBatchApi.value.getCourseBatchTreeForAffiliation(resolvedAffiliationId);
      if (response.isSuccess) {
        if (this.AffiliationCourseBatchTree === null) {
          this.AffiliationCourseBatchTree = response.data;
        } else {
          const allAffiliations = this.AffiliationCourseBatchTree.concat(response.data);
          this.AffiliationCourseBatchTree = getUniqueObjectsByKey(
            allAffiliations,
            "Id",
          ) as Array<AffiliationCourseBatch>;
        }
      } else {
        showCustomMessage({ messageText: response.errorMessage, messageType: "warning" });
      }
      this.DataLoading = false;
      return this.AffiliationCourseBatchTree;
    },
    async getActiveCourseBatchTree() {
      if (this.ActiveAffiliation === null) {
        return null;
      }
      const affiliationId = this.ActiveAffiliation.Id;
      const affiliationCourseBatchTree = await this.getAffiliationCourseBatchTree(affiliationId);
      if (affiliationCourseBatchTree === null || affiliationCourseBatchTree === undefined) {
        return null;
      }
      const courseBatchTree = deepCopyObject(
        affiliationCourseBatchTree,
      ) as Array<AffiliationCourseBatch>;
      const matchingAffiliation = courseBatchTree.find((item) => item.Id === affiliationId);
      if (matchingAffiliation === undefined) {
        return null;
      }
      return matchingAffiliation.Courses;
    },
    async getBatchesForAffiliationCourse(
      includeChildren: boolean = false,
      courseId: number | null,
      affiliationId: number | null = null,
    ) {
      if (affiliationId === null) {
        if (this.ActiveAffiliation === null) {
          return null;
        }
        affiliationId = this.ActiveAffiliation.Id;
      }
      const affiliationCourseBatchTree = await this.getAffiliationCourseBatchTree(affiliationId);
      if (affiliationCourseBatchTree === null || affiliationCourseBatchTree === undefined) {
        return null;
      }
      const courseBatchTree = deepCopyObject(
        affiliationCourseBatchTree,
      ) as Array<AffiliationCourseBatch>;
      const matchingAffiliation: Array<AffiliationCourseBatch> = includeChildren
        ? this.getFilteredCourseBatchTree(courseBatchTree, affiliationId, true)
        : this.getFilteredCourseBatchTree(courseBatchTree, affiliationId, false);
      if (matchingAffiliation.length === 0) {
        return [];
      }
      const allBatches: Array<IdNamePair> = [];
      const isMultiAffiliation = matchingAffiliation.length > 1;
      for (const affiliation of matchingAffiliation) {
        for (const course of affiliation.Courses) {
          if (courseId !== null && courseId !== course.Id) {
            continue;
          }
          for (const batch of course.Batches) {
            const batchDisplayName = isMultiAffiliation
              ? `${affiliation.Name} - ${batch.Name}`
              : batch.Name;
            allBatches.push({ Id: batch.Id, Name: batchDisplayName });
          }
        }
      }
      return allBatches;
    },
    getFilteredCourseBatchTree(
      courseBatchTree: Array<AffiliationCourseBatch>,
      affiliationId: number,
      filterByParentId: boolean,
    ) {
      const filterData = courseBatchTree.filter((item) =>
        filterByParentId
          ? item.ParentAffiliationId === affiliationId || item.Id === affiliationId
          : item.Id === affiliationId,
      );
      return filterData ? filterData : [];
    },
    async getSubjectsForBatch(batchId: number, allSubjectsTitle: string | null) {
      if (this.BatchSubjects[batchId] === undefined) {
        const apiServicesStore = useApiServicesStore();
        const { affiliationBatchApi } = storeToRefs(apiServicesStore);
        const response = await affiliationBatchApi.value.getSubjectsForBatch(batchId, true);
        this.BatchSubjects[batchId] = response.isSuccess ? response.data : [];
      }
      let batchSubjects = this.BatchSubjects[batchId];
      if (allSubjectsTitle !== null) {
        const allSubjectsOption = { Id: 0, Name: allSubjectsTitle } as IdNamePair;
        batchSubjects = [allSubjectsOption].concat(this.BatchSubjects[batchId]);
      }
      return batchSubjects;
    },
    async getAffiliationCourseForForBatch(batchId: number) {
      if (this.BatchesInfo[batchId] === undefined) {
        const apiServicesStore = useApiServicesStore();
        const { affiliationBatchApi } = storeToRefs(apiServicesStore);
        const response = await affiliationBatchApi.value.getAffiliationCourseForBatch(batchId);
        this.BatchesInfo[batchId] = response.isSuccess ? response.data : null;
      }
      return this.BatchesInfo[batchId];
    },
    getCourseNameForId(courseId: number | string): string | null {
      if (this.ActiveAffiliation === null) {
        return null;
      }
      if (typeof courseId === "string") {
        courseId = parseInt(courseId);
      }
      return (
        this.AffiliationCourses[this.ActiveAffiliation.Id]?.find((course) => course.Id === courseId)
          ?.Name || null
      );
    },
    getUserRoleOptions(excludeStudentRole: boolean = false) {
      let userRoleOptions = getIdNamePairsFromEnum(AllUserRoleIdsEnum, true);
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      if (!AllAdminRoleIds.includes(UserInfo.value.RoleId)) {
        userRoleOptions = userRoleOptions.filter((userRole) => {
          return AddEditUserRoleIds.includes(userRole.Id);
        });
      }
      if (excludeStudentRole) {
        userRoleOptions = userRoleOptions.filter((userRole) => {
          return !AllStudentRoleIds.includes(userRole.Id);
        });
      }
      return userRoleOptions;
    },
  },
});
